import { useState } from 'react';

import { Button } from '../../button';
import { Input } from '../../input';

import { Lab, Panel, Reset, Result } from './lab-parts';
import { ModelSelect } from './learning-model-surface';
import {
  CONTEXT_QUESTION,
  contextAnswer,
  contextFit,
  contextTransition,
  countWords,
  distribution,
  initialContext,
  initialKnowledge,
  initialSampling,
  initialTokens,
  knowledgeAnswer,
  knowledgePrompt,
  knowledgeQuestions,
  lessonNotes,
  noteTokens,
  SAMPLING_PREFIX,
  samplingTransition,
  temperatureLabels,
  tokenize,
  tokenSummary,
  tokenText,
  WINDOW_SIZES,
  type ContextAction,
  type KnowledgeAnswer,
  type KnowledgeQuestion,
  type SamplingAction,
  type Temperature,
  type TokenSource,
  type WindowSize,
} from './model-models';

import './relationship-labs.css';
import './model-labs.css';

function Predict({ children }: { children: string }) {
  return (
    <p className="rel-predict">
      <strong>Predict first</strong> {children}
    </p>
  );
}
function Facts({ values }: { values: [string, string][] }) {
  return (
    <dl className="cb-memory">
      {values.map(([label, value]) => (
        <div key={label}>
          <dt>{label}</dt>
          <dd>{value}</dd>
        </div>
      ))}
    </dl>
  );
}
function Toggle({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: () => void;
}) {
  return (
    <label className="cb-check">
      <input type="checkbox" checked={checked} onChange={onChange} />
      {label}
    </label>
  );
}
function Answer({ answer }: { answer: KnowledgeAnswer }) {
  return (
    <p className="model-answer">
      <q>{answer.text}</q>
      <span className="model-source" data-source={answer.source}>
        {answer.source}
      </span>
    </p>
  );
}
const sourceOptions = [
  ...lessonNotes.map((note) => ({
    value: String(note.id),
    label: `Note ${note.id}: ${note.title}`,
  })),
  { value: 'own', label: 'Your own phrase' },
];

export function ModelTokensLab() {
  const [state, setState] = useState(initialTokens);
  const text = tokenText(state);
  const pieces = tokenize(text);
  const words = countWords(text);
  return (
    <Lab
      title="How many pieces does the model see?"
      eyebrow="Language model · tokens"
      className="rel-lab model-lab"
    >
      <Predict>
        Will the model see more pieces than words? Choose a note, then press Count tokens.
      </Predict>
      <div className="cb-controls">
        <label>
          Text
          <ModelSelect
            aria-label="Text"
            value={state.source === 'own' ? 'own' : String(state.source)}
            onValueChange={(value) =>
              setState((current) => ({
                ...current,
                source: (value === 'own' ? 'own' : Number(value)) as TokenSource,
              }))
            }
            options={sourceOptions}
          />
        </label>
        {state.source === 'own' ? (
          <label>
            Your phrase
            <Input
              aria-label="Your phrase"
              value={state.phrase}
              maxLength={80}
              onChange={(event) =>
                setState((current) => ({ ...current, phrase: event.target.value }))
              }
            />
          </label>
        ) : null}
        <Reset onClick={() => setState(initialTokens())} />
      </div>
      <div className="cb-tabs" role="group" aria-label="Count by">
        <button
          type="button"
          aria-pressed={!state.countTokens}
          onClick={() => setState((current) => ({ ...current, countTokens: false }))}
        >
          Count words
        </button>
        <button
          type="button"
          aria-pressed={state.countTokens}
          onClick={() => setState((current) => ({ ...current, countTokens: true }))}
        >
          Count tokens
        </button>
      </div>
      <Panel label={state.countTokens ? 'Pieces the model sees' : 'Words, as a person counts them'}>
        {state.countTokens ? (
          <ol className="model-pieces" aria-label="Pieces">
            {pieces.map((piece, index) => (
              <li key={`${piece}-${index}`} data-kind={/^[A-Za-z]+$/.test(piece) ? 'word' : 'mark'}>
                {piece}
              </li>
            ))}
          </ol>
        ) : (
          <ol className="model-pieces" aria-label="Words">
            {text
              .split(/\s+/)
              .filter(Boolean)
              .map((word, index) => (
                <li key={`${word}-${index}`} data-kind="word">
                  {word}
                </li>
              ))}
          </ol>
        )}
      </Panel>
      <Facts
        values={[
          ['Words', String(words)],
          ...(state.countTokens ? [['Tokens', String(pieces.length)] as [string, string]] : []),
        ]}
      />
      <Result tone={state.countTokens ? 'success' : 'neutral'}>{tokenSummary(state)}</Result>
      <p className="rel-caption">A simulation of a tokenizer.</p>
    </Lab>
  );
}

export function ModelSamplingLab() {
  const [state, setState] = useState(initialSampling);
  const act = (action: SamplingAction) =>
    setState((current) => samplingTransition(current, action));
  const chances = distribution(state.temperature);
  const tokens = new Set(state.picks.map((pick) => pick.token));
  return (
    <Lab
      title="Why does the same prompt give different endings?"
      eyebrow="Language model · the next token"
      className="rel-lab model-lab"
    >
      <Predict>
        Will three runs end the same way? Press Pick the next token three times, then set
        Temperature to Low.
      </Predict>
      <div className="cb-controls">
        <label>
          Temperature
          <ModelSelect
            aria-label="Temperature"
            value={state.temperature}
            onValueChange={(value) => act({ type: 'temperature', value: value as Temperature })}
            options={(Object.keys(temperatureLabels) as Temperature[]).map((key) => ({
              value: key,
              label: temperatureLabels[key],
            }))}
          />
        </label>
        <Button size="sm" onClick={() => act({ type: 'pick' })}>
          Pick the next token
        </Button>
        <Reset onClick={() => act({ type: 'reset' })} />
      </div>
      <Panel label={`Chances for the token after “${SAMPLING_PREFIX}”`}>
        <ol className="model-chances" aria-label="Chances">
          {chances.map((chance) => (
            <li key={chance.token}>
              <code>{chance.token}</code>
              <span className="model-bar" aria-hidden="true">
                <span style={{ width: `${chance.percent}%` }} />
              </span>
              <span>{chance.percent}%</span>
            </li>
          ))}
        </ol>
      </Panel>
      <Panel label="Runs so far">
        <pre aria-label="Runs so far">
          {state.picks.length
            ? state.picks
                .map(
                  (pick) =>
                    `Run ${pick.run}: draw ${pick.draw.toFixed(2)} → "${pick.token}" → ${SAMPLING_PREFIX} ${pick.continuation}`,
                )
                .join('\n')
            : '(no runs yet)'}
        </pre>
      </Panel>
      <Facts
        values={[
          ['Runs', String(state.picks.length)],
          ['Different endings', String(tokens.size)],
        ]}
      />
      <Result tone={tokens.size > 1 ? 'warning' : state.picks.length ? 'success' : 'neutral'}>
        {state.message}
      </Result>
      <p className="rel-caption">A simulation of next-token sampling.</p>
    </Lab>
  );
}

export function ModelKnowledgeLab() {
  const [state, setState] = useState(initialKnowledge);
  const answer = knowledgeAnswer(state);
  return (
    <Lab
      title="Can the model answer without being told?"
      eyebrow="Language model · what it knows"
      className="rel-lab model-lab"
    >
      <Predict>
        Will the model admit it does not know? Read the answer, then check Include the task list in
        the prompt.
      </Predict>
      <div className="cb-controls">
        <label>
          Question
          <ModelSelect
            aria-label="Question"
            value={state.question}
            onValueChange={(value) =>
              setState((current) => ({ ...current, question: value as KnowledgeQuestion }))
            }
            options={(Object.keys(knowledgeQuestions) as KnowledgeQuestion[]).map((key) => ({
              value: key,
              label: knowledgeQuestions[key],
            }))}
          />
        </label>
      </div>
      <div className="cb-controls">
        <Toggle
          label="Include the task list in the prompt"
          checked={state.includeTasks}
          onChange={() =>
            setState((current) => ({ ...current, includeTasks: !current.includeTasks }))
          }
        />
        <Toggle
          label="Include today's date in the prompt"
          checked={state.includeDate}
          onChange={() =>
            setState((current) => ({ ...current, includeDate: !current.includeDate }))
          }
        />
        <Reset onClick={() => setState(initialKnowledge())} />
      </div>
      <div className="cb-two">
        <Panel label="Prompt the model receives">
          <pre aria-label="Prompt">{knowledgePrompt(state).join('\n')}</pre>
        </Panel>
        <Panel label="Answer">
          <Answer answer={answer} />
        </Panel>
      </div>
      <Result tone={answer.source === 'guess' ? 'warning' : 'success'}>{answer.explanation}</Result>
      <p className="rel-caption">A simulation with fixed answers.</p>
    </Lab>
  );
}

export function ModelContextLab() {
  const [state, setState] = useState(initialContext);
  const act = (action: ContextAction) => setState((current) => contextTransition(current, action));
  const fit = contextFit(state);
  const answer = contextAnswer(state);
  return (
    <Lab
      title="What happens when the notes do not fit?"
      eyebrow="Language model · the context window"
      className="rel-lab model-lab"
    >
      <Predict>
        Which note survives when both do not fit? Press Add note 1, then Add note 2.
      </Predict>
      <div className="cb-controls">
        <label>
          Window size
          <ModelSelect
            aria-label="Window size"
            value={String(state.window)}
            onValueChange={(value) => act({ type: 'window', value: Number(value) as WindowSize })}
            options={WINDOW_SIZES.map((size) => ({ value: String(size), label: `${size} tokens` }))}
          />
        </label>
        {lessonNotes.map((note) => (
          <Button
            key={note.id}
            size="sm"
            variant={note.id === 1 ? 'primary' : 'secondary'}
            disabled={state.added.includes(note.id)}
            onClick={() => act({ type: 'add', note: note.id })}
          >
            Add note {note.id}
          </Button>
        ))}
        <Reset onClick={() => act({ type: 'reset' })} />
      </div>
      <div
        className="model-window"
        role="img"
        aria-label={
          fit.inside.length
            ? `Window of ${state.window} tokens holding note${fit.inside.length === 1 ? '' : 's'} ${fit.inside.join(', ')}; ${fit.used} tokens used`
            : `Empty window of ${state.window} tokens`
        }
      >
        {fit.inside.map((id) => (
          <span key={id} style={{ width: `${(noteTokens(id) / state.window) * 100}%` }}>
            Note {id} · {noteTokens(id)}
          </span>
        ))}
      </div>
      <Facts
        values={[
          ['Tokens used', `${fit.used} of ${state.window}`],
          [
            'Notes inside',
            fit.inside.length ? fit.inside.map((id) => `note ${id}`).join(', ') : 'none',
          ],
          ...(fit.dropped.length
            ? [
                ['Notes that fell out', fit.dropped.map((id) => `note ${id}`).join(', ')] as [
                  string,
                  string,
                ],
              ]
            : []),
        ]}
      />
      <div className="cb-two">
        <Panel label="Question">
          <p className="model-answer">{CONTEXT_QUESTION}</p>
        </Panel>
        <Panel label="Answer">
          <Answer answer={answer} />
        </Panel>
      </div>
      <Result tone={answer.source === 'guess' ? 'warning' : 'success'}>
        {state.message} {answer.explanation}
      </Result>
      <p className="rel-caption">A simulation of a context window.</p>
    </Lab>
  );
}
